/*
 * Alternate App entrypoint for onboarding flows that are rendered within another webpage (i.e.
 * our Webflow page).
 */

import OnboardingScreen from '@/screens/OnboardingScreen';
import {materialTheme} from '@/styles/theme';
import getClient from '@/utils/apolloClient';
import featureFlags, {FEATURES_ENDPOINT} from '@/utils/featureFlags';
import {
  identifyLanguage,
  Locales,
  LocalizationContext,
} from '@/utils/localization';
import {ApolloProvider} from '@apollo/client';
import {GrowthBookProvider} from '@growthbook/growthbook-react';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import {enUS, esES, zhCN} from '@mui/material/locale';
import {
  adaptV4Theme,
  createTheme,
  StyledEngineProvider,
  ThemeProvider,
} from '@mui/material/styles';
import React, {useEffect, useState} from 'react';
import {Route, BrowserRouter as Router, Switch} from 'react-router-dom';
import {QueryParamProvider, StringParam, useQueryParam} from 'use-query-params';
// We don't currently do anything with logged in users in firebase, but this import is required
// because we share apollo client code (to at least check whether there's a user and pass that
// info on to the backend if there is one authorized).
import '@/utils/connectFirebase';

function OnboardingApp({flowName, maxHeight}) {
  const [language] = useQueryParam('lg', StringParam);

  // We use state since the query params and history are controlled downstream
  // by OnboardingPanel and LanguagePicker
  const [locale, setLocale] = useState(identifyLanguage(language));
  const [client, setClient] = useState(undefined);

  useEffect(() => {
    getClient().then(client => {
      setClient(client);
    });

    // Load feature flags
    fetch(FEATURES_ENDPOINT)
      .then(featureFlagsResult => featureFlagsResult.json())
      .then(json => featureFlags.setFeatures(json.features))
      .catch(error => console.error('Failed to load feature flags', error));
    featureFlags.setAttributes({
      // Never logged in in ello-public
      loggedIn: false,
    });
  }, []);

  const theme = createTheme(
    adaptV4Theme(
      materialTheme,
      {[Locales.enUS]: enUS, [Locales.zhCN]: zhCN, [Locales.esES]: esES}[
        locale
      ],
    ),
  );

  if (!client) {
    return (
      <CircularProgress
        style={{
          marginTop: '40px',
          marginBottom: '40px',
          marginLeft: 'auto',
          marginRight: 'auto',
          display: 'block',
        }}
      />
    );
  }

  return (
    <GrowthBookProvider growthbook={featureFlags}>
      <StyledEngineProvider>
        <ThemeProvider theme={theme}>
          <LocalizationContext.Provider value={{locale, setLocale}}>
            <CssBaseline />
            <ApolloProvider client={client}>
              <Router>
                <Switch>
                  <Route path="/" exact>
                    <QueryParamProvider ReactRouterRoute={Route}>
                      <OnboardingScreen
                        flowName={flowName}
                        maxHeight={maxHeight}
                        locale={locale}
                      />
                    </QueryParamProvider>
                  </Route>
                  <Route path="/healthz" exact>
                    <h3>Healthy</h3>
                  </Route>
                </Switch>
              </Router>
            </ApolloProvider>
          </LocalizationContext.Provider>
        </ThemeProvider>
      </StyledEngineProvider>
    </GrowthBookProvider>
  );
}

export default OnboardingApp;
